import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../state/authStore'
import { useState } from 'react'

/**
 * EmailVerificationBanner - Bannière d'avertissement pour les comptes non vérifiés
 * Affiche un message encourageant l'utilisateur à vérifier son email
 */
export function EmailVerificationBanner() {
  const navigate = useNavigate()
  const { user, resendVerification } = useAuthStore()
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)

  // Ne rien afficher si :
  // - pas d'utilisateur
  // - email déjà vérifié
  // - bannière fermée par l'utilisateur
  if (!user || user.email_verified || isDismissed) {
    return null
  }

  const handleResend = async () => {
    if (isResending) return
    setIsResending(true)
    setResendSuccess(false)

    try {
      await resendVerification(user!.email)
      setResendSuccess(true)
      setTimeout(() => setResendSuccess(false), 5000)
    } catch (error) {
      console.error('Erreur lors du renvoi du code:', error)
    } finally {
      setIsResending(false)
    }
  }

  const handleGoToVerify = () => {
    navigate('/verify-email-sent', { state: { email: user!.email } })
  }

  return (
    <div className="bg-yellow-50 border-b border-yellow-200 shadow-sm">
      <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="w-0 flex-1 flex items-center">
            {/* Icône */}
            <span className="flex p-2 rounded-lg bg-yellow-100">
              <svg
                className="h-5 w-5 text-yellow-800"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </span>

            {/* Message */}
            <p className="ml-3 font-medium text-yellow-800 text-sm">
              <span className="md:hidden">Vérifiez votre email</span>
              <span className="hidden md:inline">
                Veuillez vérifier votre adresse email pour accéder à toutes les fonctionnalités.
              </span>
            </p>
          </div>

          {/* Actions */}
          <div className="order-3 mt-2 flex-shrink-0 w-full sm:order-2 sm:mt-0 sm:w-auto">
            {resendSuccess ? (
              <span className="text-sm text-green-700 font-medium">✓ Email renvoyé !</span>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleGoToVerify}
                  className="flex items-center justify-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-yellow-800 bg-yellow-100 hover:bg-yellow-200 transition-colors"
                >
                  Saisir le code
                </button>
                <button
                  onClick={handleResend}
                  disabled={isResending}
                  className="flex items-center justify-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isResending ? 'Envoi...' : "Renvoyer l'email"}
                </button>
              </div>
            )}
          </div>

          {/* Bouton fermer */}
          <div className="order-2 flex-shrink-0 sm:order-3 sm:ml-3">
            <button
              type="button"
              onClick={() => setIsDismissed(true)}
              className="-mr-1 flex p-2 rounded-md hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-600 transition-colors"
            >
              <span className="sr-only">Fermer</span>
              <svg
                className="h-5 w-5 text-yellow-800"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
